import type { ComponentType } from "react";
import { ApprovalCardTool } from "./ApprovalCardTool";
import { AskUserQuestionCard } from "./AskUserQuestionCard";
import { PublicDatasetTriggerCard } from "./PublicDatasetTriggerCard";
import type { ToolCallEntry } from "./useTurns";

// Props every human-in-the-loop tool card receives. The card reads the pending
// call from `entry` (interactive while `entry.result` is undefined, read-only
// once set) and submits the user's response via `onAnswer`, which appends a
// tool message and resumes the paused run.
export interface HitlToolProps {
  entry: ToolCallEntry;
  // Whether the agent run is still in flight. Lets a card tell "the tool-call
  // args are still streaming in" (show a placeholder) apart from "the args are
  // final but unparseable" (a real error to surface), rather than guessing.
  isRunning: boolean;
  // Resume the paused run with the user's response. Omitted in read-only
  // views (shared / admin), where the card renders but cannot be answered.
  // Resolves when the resume is sent; rejects if it fails, so the card can
  // re-enable Submit for a retry rather than latching on "Submitting…".
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
  // The other tool calls in the same assistant turn, in order. Lets a card
  // render from a preceding tool's result — e.g. `PublicDatasetTriggerCard`
  // resolves a DAG's display name from the turn's `list_pipelines` result rather
  // than trusting the model to author it.
  turnToolCalls?: readonly ToolCallEntry[];
}

// The backend tools that can pause a run awaiting human input (deferred or
// approval-required in pydantic-ai terms). Hardcoded on purpose: only these
// names ever block the composer or render an answer card. An un-resulted call
// from any other tool is the residue of an interrupted run, never a pause.
// Adding a HITL tool to the backend requires adding its name here.
export const HITL_TOOL_NAMES: ReadonlySet<string> = new Set([
  "ask_user_question",
  "ask_for_approval",
  "pipeline_submit_run",
  "pipeline_cancel_run",
  "public_dataset_trigger_run",
]);

// Bespoke cards, keyed by tool name. A HITL tool without an entry here gets
// the generic DeferredToolFallbackCard (free-text answer) instead.
export const HITL_TOOL_RENDERERS: Record<string, ComponentType<HitlToolProps>> = {
  ask_user_question: AskUserQuestionCard,
  ask_for_approval: ApprovalCardTool,
  public_dataset_trigger_run: PublicDatasetTriggerCard,
};

export const isHitlTool = (name: string): boolean => HITL_TOOL_NAMES.has(name);
